import React, { memo, useCallback, useContext } from 'react'

import {
  Usage, Button, Icon, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openUsageModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

function UsageButton({ isOpen = true }) {
  const t = useTranslation()
  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const isUsageModalOpen = useThreeDStudioStore(state => state.ui.isUsageModalOpen)
  const { extractingCoreStyles, pageConfigLoaded, userLoggedIn } = useContext(ThreeDStudioPageContext)

  const handleClick = useCallback(() => {
    openUsageModal()
  }, [])

  // On this fast page login is unknowable at SSR, so render nothing until pageConfig
  // positively confirms a logged-in visitor. Rendering during the pending window flashed
  // the disabled button into the footer, then removed it once pageConfig resolved as
  // anonymous - the majority / CrUX path. Defaulting to hidden accepts a single in-place
  // appearance for logged-in users when pageConfig lands; the footer is a fixed 41px so
  // this adds no layout shift. During extraction the tier is not user-specific, so render.
  if (!extractingCoreStyles && (!pageConfigLoaded || !userLoggedIn)) {
    return null
  }

  const isTierPending = !studioAccessTier && !extractingCoreStyles

  const button = (
    <Button
      type="ghost"
      variant={isOpen ? 'with-icon' : 'only-icon'}
      icon={<Icon svgComponent={Usage} color="darker" notSpaced />}
      customClass={classnames('usage-button', !isOpen && 'usage-button--collapsed')}
      onClick={handleClick}
      disabled={isTierPending}
      aria-label="Usage"
      aria-haspopup="dialog"
      aria-expanded={isUsageModalOpen}
    >
      {isOpen ? t('sidebar.usage.label') : null}
    </Button>
  )

  if (isOpen) {
    return button
  }

  return (
    <DesktopOnlyTooltip
      content={t('sidebar.usage.label')}
      id="usage-button-tooltip"
      place="right"
      containerClass="usage-button-tooltip"
      className="usage-button-tooltip__trigger"
    >
      {button}
    </DesktopOnlyTooltip>
  )
}

UsageButton.propTypes = {
  isOpen: bool,
}

export default memo(UsageButton)
